/**
 * Hook para gerenciar reprodução de mídia sincronizada com segmentos de transcrição.
 * 
 * Carrega a URL presigned do backend e expõe estado reativo para sincronizar a UI
 * (segmento ativo, progresso, etc.).
 * 
 * O controle efetivo do áudio é feito pelo react-h5-audio-player no componente MediaPlayer.
 * Este hook gerencia:
 *   - Carregamento e refresh automático da URL presigned do S3
 *   - Estado de reprodução (currentTime, duration, isPlaying) atualizado via callbacks
 *   - Ações externas (seekTo, play, pause) que operam no elemento <audio> via ref
 * 
 * A mídia é retida temporariamente no S3 (padrão 72h após transcrição).
 * Quando expirada, mediaAvailable=false e o player exibe aviso adequado.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { getMediaUrl, MediaUrlResponse } from '../../../services/api'

export type MediaPlayerState = {
  /** Se a mídia está disponível no S3 (dentro do TTL) */
  mediaAvailable: boolean
  /** URL presigned para streaming (null se indisponível) */
  mediaUrl: string | null
  /** Content-Type da mídia (audio/mpeg, video/mp4, etc.) */
  contentType: string | null
  /** Mensagem do backend (ex: "Mídia expirada") */
  message: string | null
  /** Tempo restante da URL presigned em segundos */
  expiresIn: number
  /** Se está carregando a URL do backend */
  isLoading: boolean
  /** Se a mídia está em reprodução */
  isPlaying: boolean
  /** Tempo atual de reprodução em segundos */
  currentTime: number
  /** Duração total da mídia em segundos */
  duration: number
  /** Volume (0-1) */
  volume: number
  /** Se o elemento de mídia está buffering */
  isBuffering: boolean
  /** Erro de carregamento/reprodução */
  error: string | null
  /** Ref para o elemento HTML5 <audio> (gerenciado pelo react-h5-audio-player) */
  mediaRef: React.MutableRefObject<HTMLAudioElement | null>
}

export type MediaPlayerActions = {
  /** Inicia reprodução */
  play: () => void
  /** Pausa reprodução */
  pause: () => void
  /** Alterna play/pause */
  toggle: () => void
  /** Pula para um timestamp específico (em segundos) */
  seekTo: (seconds: number) => void
  /** Define volume (0–1) */
  setVolume: (volume: number) => void
  /** Recarrega a URL presigned (útil se expirar durante uso) */
  refreshUrl: () => Promise<void>
  /** Reseta o player: pausa e volta ao início (currentTime=0) */
  reset: () => void
  /** Reproduz apenas o trecho de um segmento (startTime→endTime), pausando automaticamente ao final */
  playSegment: (startTime: number, endTime: number) => void
  /** Cancela reprodução de segmento em andamento */
  stopSegmentPlayback: () => void
  /** Tempo final do segmento em reprodução (null se nenhum segmento ativo) */
  segmentPlayingEndTime: number | null
}

/** Callbacks internos — usados pelo componente MediaPlayer para atualizar estado do hook */
export type MediaPlayerCallbacks = {
  _onTimeUpdate: (time: number) => void
  _onDurationChange: (duration: number) => void
  _onPlayStateChange: (playing: boolean) => void
  _onBufferingChange: (buffering: boolean) => void
  _onError: (error: string | null) => void
  _clearError: () => void
}

export type UseMediaPlayerReturn = MediaPlayerState & MediaPlayerActions & MediaPlayerCallbacks

export default function useMediaPlayer(fileId: string | null): UseMediaPlayerReturn {
  const [mediaAvailable, setMediaAvailable] = useState(false)
  const [mediaUrl, setMediaUrl] = useState<string | null>(null)
  const [contentType, setContentType] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [expiresIn, setExpiresIn] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolumeState] = useState(0.8)
  const [isBuffering, setIsBuffering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Ref para o elemento <audio> — atribuído pelo MediaPlayer a partir do react-h5-audio-player
  const mediaRef = useRef<HTMLAudioElement | null>(null)
  const urlExpiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // --- URL Loading ---

  const loadMediaUrl = useCallback(async () => {
    if (!fileId) return

    setIsLoading(true)
    setError(null)

    try {
      const response: MediaUrlResponse = await getMediaUrl(fileId)

      setMediaAvailable(response.mediaAvailable)
      setMediaUrl(response.mediaUrl)
      setContentType(response.contentType)
      setMessage(response.message)
      setExpiresIn(response.expiresIn)

      if (!response.mediaAvailable) {
        setIsPlaying(false)
      }

      // Agenda refresh automático 30s antes de expirar
      if (response.mediaAvailable && response.expiresIn > 60) {
        if (urlExpiryTimerRef.current) clearTimeout(urlExpiryTimerRef.current)
        urlExpiryTimerRef.current = setTimeout(() => {
          if (mediaRef.current && !mediaRef.current.paused) {
            loadMediaUrl()
          }
        }, (response.expiresIn - 30) * 1000)
      }
    } catch (e: any) {
      console.error('Erro ao carregar URL de mídia:', e)
      setMediaAvailable(false)
      setError(e.message || 'Não foi possível carregar a mídia')
    } finally {
      setIsLoading(false)
    }
  }, [fileId])

  // Carrega na montagem
  useEffect(() => {
    loadMediaUrl()
    return () => {
      if (urlExpiryTimerRef.current) clearTimeout(urlExpiryTimerRef.current)
    }
  }, [loadMediaUrl])

  // --- Ações (operam no <audio> element via ref) ---

  const play = useCallback(() => {
    mediaRef.current?.play().catch(() => {
      setError('Não foi possível iniciar a reprodução')
    })
  }, [])

  const pause = useCallback(() => {
    mediaRef.current?.pause()
  }, [])

  const toggle = useCallback(() => {
    if (mediaRef.current?.paused) {
      play()
    } else {
      pause()
    }
  }, [play, pause])

  const seekTo = useCallback((seconds: number) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = Math.max(0, Math.min(seconds, duration || Infinity))
      setCurrentTime(mediaRef.current.currentTime)
    }
  }, [duration])

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v))
    setVolumeState(clamped)
    if (mediaRef.current) {
      mediaRef.current.volume = clamped
    }
  }, [])

  const refreshUrl = useCallback(async () => {
    await loadMediaUrl()
  }, [loadMediaUrl])

  const reset = useCallback(() => {
    mediaRef.current?.pause()
    if (mediaRef.current) {
      mediaRef.current.currentTime = 0
    }
    setCurrentTime(0)
    setIsPlaying(false)
    segmentEndTimeRef.current = null
    setSegmentPlayingEndTime(null)
  }, [])

  // --- Reprodução de segmento (trecho específico) ---

  const segmentEndTimeRef = useRef<number | null>(null)
  const [segmentPlayingEndTime, setSegmentPlayingEndTime] = useState<number | null>(null)

  const stopSegmentPlayback = useCallback(() => {
    mediaRef.current?.pause()
    segmentEndTimeRef.current = null
    setSegmentPlayingEndTime(null)
  }, [])

  const playSegment = useCallback((startTime: number, endTime: number) => {
    if (!mediaRef.current) return
    // Cancela reprodução de segmento anterior
    segmentEndTimeRef.current = endTime
    setSegmentPlayingEndTime(endTime)
    // Seek para o início do segmento e reproduz
    mediaRef.current.currentTime = Math.max(0, startTime)
    setCurrentTime(startTime)
    mediaRef.current.play().catch(() => {
      setError('Não foi possível iniciar a reprodução do segmento')
      segmentEndTimeRef.current = null
      setSegmentPlayingEndTime(null)
    })
  }, [])

  // --- Callbacks internos (chamados pelo MediaPlayer via eventos do react-h5-audio-player) ---

  const _onTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time)
    // Auto-pause ao atingir o fim do segmento
    if (segmentEndTimeRef.current !== null && time >= segmentEndTimeRef.current) {
      mediaRef.current?.pause()
      segmentEndTimeRef.current = null
      setSegmentPlayingEndTime(null)
    }
  }, [])

  const _onDurationChange = useCallback((d: number) => {
    if (d && isFinite(d) && d > 0) setDuration(d)
  }, [])

  const _onPlayStateChange = useCallback((playing: boolean) => setIsPlaying(playing), [])

  const _onBufferingChange = useCallback((buffering: boolean) => setIsBuffering(buffering), [])

  const _onError = useCallback((e: string | null) => setError(e), [])

  const _clearError = useCallback(() => setError(null), [])

  return {
    mediaAvailable,
    mediaUrl,
    contentType,
    message,
    expiresIn,
    isLoading,
    isPlaying,
    currentTime,
    duration,
    volume,
    isBuffering,
    error,
    mediaRef,
    play,
    pause,
    toggle,
    seekTo,
    setVolume,
    refreshUrl,
    reset,
    playSegment,
    stopSegmentPlayback,
    segmentPlayingEndTime,
    _onTimeUpdate,
    _onDurationChange,
    _onPlayStateChange,
    _onBufferingChange,
    _onError,
    _clearError
  }
}
